import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Input, Progress, Select, Space, Statistic, Table, Tag, message } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import apiClient from '../../services/apiClient';
import GcpResourceDetailDrawer from './GcpResourceDetailDrawer';

type Props = {
  provider?: 'aws' | 'azure' | 'gcp';
  accountId?: string;
  reportId?: string;
  open: boolean;
  onClose: () => void;
  initialAssetType?: string;
  initialProjectId?: string;
  initialProjectName?: string;
};

const filterFields = ['projectId', 'service', 'assetType', 'category', 'location', 'status', 'costSource', 'costAllocation'] as const;

const GcpInventoryDrawer: React.FC<Props> = ({
  provider = 'gcp',
  accountId,
  reportId,
  open,
  onClose,
  initialAssetType,
  initialProjectId,
  initialProjectName
}) => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [facets, setFacets] = useState<Record<string, string[]>>({});
  const [totals, setTotals] = useState<any>({});
  const [reconciliation, setReconciliation] = useState<any>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [filters, setFilters] = useState<Record<string, any>>({ sortBy: 'monthlyCost', sortOrder: 'desc' });
  const [searchInput, setSearchInput] = useState('');
  const [selectedResource, setSelectedResource] = useState<any | null>(null);
  const moneyFormatter = useMemo(() => new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: reconciliation?.currency || 'BRL'
  }), [reconciliation?.currency]);
  const hasFreshAllocation = reconciliation?.allocationStatus !== 'requires-full-inventory-refresh';

  useEffect(() => {
    if (!open) return;
    setSelectedResource(null);
    setSearchInput('');
    setPagination(current => ({ ...current, page: 1 }));
    setFilters({
      sortBy: 'monthlyCost',
      sortOrder: 'desc',
      assetType: initialAssetType || undefined,
      projectId: initialProjectId || undefined
    });
  }, [initialAssetType, initialProjectId, open]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters(current => ({ ...current, search: searchInput, page: 1 }));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadInventory = useCallback(async () => {
    if (!open || !accountId) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/cloud/accounts/${encodeURIComponent(accountId)}/inventory`, {
        params: { ...filters, reportId, page: pagination.page, limit: pagination.limit }
      });
      setItems(response.data?.items || []);
      setFacets(response.data?.facets || {});
      setTotals(response.data?.totals || {});
      setReconciliation(response.data?.reconciliation || null);
      setPagination(current => ({
        ...current,
        page: response.data?.pagination?.page || current.page,
        total: response.data?.pagination?.total || 0
      }));
    } catch (error: any) {
      message.error(error?.response?.data?.details || 'Não foi possível carregar o inventário.');
    } finally {
      setLoading(false);
    }
  }, [accountId, filters, open, pagination.limit, pagination.page, reportId]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  const updateFilter = (field: string, value: any) => {
    setPagination(current => ({ ...current, page: 1 }));
    setFilters(current => ({ ...current, [field]: value || undefined }));
  };

  const exportCsv = async () => {
    if (!accountId) return;
    setExporting(true);
    try {
      const response = await apiClient.get(`/cloud/accounts/${encodeURIComponent(accountId)}/inventory.csv`, {
        params: { ...filters, reportId },
        responseType: 'blob'
      });
      const disposition = response.headers['content-disposition'] || '';
      const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || `inventario-${provider}.csv`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(error?.response?.data?.details || 'Não foi possível exportar o inventário.');
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo(() => [
    { title: 'Recurso', dataIndex: 'name', key: 'name', width: 260, sorter: true, fixed: 'left' as const,
      render: (value: string, row: any) => <div><strong>{value}</strong><small className="gcp-inventory__secondary">{row.resourceId}</small></div> },
    { title: provider === 'azure' ? 'Subscription' : provider === 'aws' ? 'Conta' : 'Projeto', dataIndex: 'projectId', key: 'projectId', width: 190, sorter: true },
    { title: 'Tipo', dataIndex: 'assetType', key: 'assetType', width: 260, sorter: true },
    { title: 'Local', dataIndex: 'location', key: 'location', width: 150, sorter: true },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 130, sorter: true, render: (value: string) => <Tag>{value}</Tag> },
    { title: 'vCPU', dataIndex: 'vcpu', key: 'vcpu', width: 85, sorter: true },
    { title: 'Memória', dataIndex: 'memoryGb', key: 'memoryGb', width: 105, sorter: true, render: (value: number) => value == null ? '—' : `${value} GB` },
    { title: 'CPU média', dataIndex: 'cpuAveragePercent', key: 'cpuAveragePercent', width: 115, sorter: true, render: (value: number) => value == null ? '—' : `${value.toFixed(1)}%` },
    { title: 'Disco', dataIndex: 'diskSizeGb', key: 'diskSizeGb', width: 100, sorter: true, render: (value: number) => value == null ? '—' : `${value} GB` },
    { title: 'Custo mensal', dataIndex: 'monthlyCost', key: 'monthlyCost', width: 145, sorter: true,
      render: (value: number, row: any) => value == null
        ? <span>Sem custo direto</span>
        : <div><strong>{moneyFormatter.format(value)}</strong><small className="gcp-inventory__secondary">{row.costAllocation === 'billing-only' ? 'Somente no faturamento' : row.costAllocation === 'shared' ? 'Custo compartilhado' : 'Custo direto'} • {row.costSource === 'billing-export' ? 'Billing Export' : row.costSource}</small></div> },
    { title: 'Custo do período', dataIndex: 'periodCost', key: 'periodCost', width: 150, sorter: true, render: (value: number) => value == null ? '—' : moneyFormatter.format(value) }
  ], [moneyFormatter, provider]);

  return (
    <Drawer
      title={initialProjectId
        ? `Recursos do projeto ${initialProjectName || initialProjectId}`
        : initialAssetType
          ? `Detalhes dos recursos ${provider.toUpperCase()}`
          : `Inventário completo ${provider.toUpperCase()}`}
      open={open}
      onClose={onClose}
      width="94vw"
      destroyOnHidden
    >
      {!accountId ? <div>Este relatório não possui uma conta conectada associada.</div> : <>
        {reconciliation ? (
          <div className="gcp-inventory__reconciliation">
            <div className="gcp-inventory__reconciliation-head">
              <div>
                <strong>Reconciliação financeira</strong>
                <span>{reconciliation.periodLabel} • {reconciliation.monthsAnalyzed} mês(es)</span>
              </div>
              <Tag color={reconciliation.status === 'reconciled' ? 'green' : reconciliation.status === 'divergent' ? 'red' : 'orange'}>
                {reconciliation.status === 'reconciled' ? 'Reconciliado' : reconciliation.status === 'divergent' ? 'Divergente' : 'Detalhamento indisponível'}
              </Tag>
            </div>
            <div className="gcp-inventory__totals">
              <Statistic title="Média mensal fechada" value={reconciliation.officialMonthlyAverage || 0} formatter={value => moneyFormatter.format(Number(value))} />
              <Statistic title="Total oficial do período" value={reconciliation.officialPeriodCost || 0} formatter={value => moneyFormatter.format(Number(value))} />
              <Statistic title="Export detalhado do período" value={reconciliation.resourceExportPeriodCost || 0} formatter={value => moneyFormatter.format(Number(value))} />
              {hasFreshAllocation && <Statistic title="Atribuído mensalmente" value={reconciliation.assignedMonthlyAverage || 0} formatter={value => moneyFormatter.format(Number(value))} />}
              {hasFreshAllocation && <Statistic title="Somente no faturamento" value={reconciliation.billingOnlyPeriodCost || 0} formatter={value => moneyFormatter.format(Number(value))} />}
              {hasFreshAllocation && <Statistic title="Custos compartilhados" value={reconciliation.unnamedSharedPeriodCost || 0} formatter={value => moneyFormatter.format(Number(value))} />}
              {hasFreshAllocation && <Statistic title="Não vinculado a recurso ativo/mês" value={reconciliation.unattributedMonthlyAverage || 0} formatter={value => moneyFormatter.format(Number(value))} />}
              {hasFreshAllocation && reconciliation.unexplainedPeriodCost != null && <Statistic title="Variação residual" value={reconciliation.unexplainedPeriodCost || 0} formatter={value => moneyFormatter.format(Number(value))} />}
              <Statistic title="Diferença entre exports" value={reconciliation.reconciliationDifference || 0} formatter={value => moneyFormatter.format(Number(value))} />
            </div>
            {hasFreshAllocation && <Progress percent={Number(reconciliation.coveragePercent || 0)} status={reconciliation.status === 'divergent' ? 'exception' : 'normal'} format={percent => `${Number(percent || 0).toFixed(1)}% atribuído`} />}
            {hasFreshAllocation && reconciliation.explainabilityPercent != null && <Progress percent={Number(reconciliation.explainabilityPercent || 0)} status="normal" strokeColor="#13c2c2" format={percent => `${Number(percent || 0).toFixed(1)}% explicado`} />}
            {!hasFreshAllocation && (
              <Alert type="info" showIcon message="Totais atualizados; atribuição por recurso preservada" description="A comparação entre os exports foi atualizada agora. Execute uma coleta completa para recalcular a cobertura do inventário por recurso." />
            )}
            {reconciliation.status === 'divergent' && (
              <Alert type="error" showIcon message="Os totais dos exports não estão reconciliados" description={`A diferença de ${moneyFormatter.format(Number(reconciliation.reconciliationDifference || 0))} ultrapassa a tolerância de ${moneyFormatter.format(Number(reconciliation.tolerance || 0))}. Verifique atualização e período das tabelas do Billing Export.`} />
            )}
            {hasFreshAllocation && Array.isArray(reconciliation.unattributedByService) && reconciliation.unattributedByService.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <strong>Custos sem identificação de recurso no Billing Export</strong>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="serviceName"
                  dataSource={reconciliation.unattributedByService}
                  columns={[
                    { title: 'Serviço', dataIndex: 'serviceName', key: 'serviceName' },
                    { title: 'Custo do período', dataIndex: 'periodCost', key: 'periodCost', align: 'right', render: (value: number) => moneyFormatter.format(Number(value || 0)) }
                  ]}
                />
              </div>
            )}
          </div>
        ) : (
          <Alert type="info" showIcon message="Reconciliação disponível após uma nova coleta" description="Este snapshot foi gerado antes da validação integral dos custos do Billing Export." />
        )}
        <div className="gcp-inventory__totals">
          <Statistic title="Recursos filtrados" value={totals.resources || 0} />
          <Statistic title="Com custo direto" value={totals.resourcesWithCost || 0} />
          <Statistic title="Sem custo identificado" value={totals.resourcesWithoutCost || 0} />
          <Statistic title="Custo mensal identificado" value={totals.monthlyCost || 0} formatter={value => moneyFormatter.format(Number(value))} />
        </div>
        <div className="gcp-inventory__filters">
          <Input allowClear prefix={<SearchOutlined />} placeholder="Buscar nome, ID, tipo ou projeto" value={searchInput} onChange={event => setSearchInput(event.target.value)} />
          {filterFields.map(field => (
            <Select
              key={field}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={{ projectId: 'Projeto', service: 'Serviço', assetType: 'Tipo', category: 'Categoria', location: 'Local', status: 'Status', costSource: 'Origem do custo', costAllocation: 'Tipo de atribuição' }[field]}
              options={(facets[field] || []).map(value => ({ label: value, value }))}
              value={filters[field]}
              onChange={value => updateFilter(field, value)}
            />
          ))}
          <Select allowClear placeholder="Cobertura financeira" value={filters.hasCost} onChange={value => updateFilter('hasCost', value)} options={[
            { label: 'Com custo direto', value: 'true' },
            { label: 'Sem custo identificado', value: 'false' }
          ]} />
          <Space>
            <Button onClick={() => {
              setSearchInput('');
              setFilters({
                sortBy: 'monthlyCost',
                sortOrder: 'desc',
                projectId: initialProjectId || undefined,
                assetType: initialAssetType || undefined
              });
              setPagination(current => ({ ...current, page: 1 }));
            }}>Limpar</Button>
            <Button type="primary" icon={<DownloadOutlined />} loading={exporting} onClick={exportCsv}>Exportar filtrado</Button>
          </Space>
        </div>
        <Table
          rowKey={(row: any) => `${row.projectId}-${row.resourceId}-${row.assetType}`}
          loading={loading}
          columns={columns}
          dataSource={items}
          scroll={{ x: 1550, y: 'calc(100vh - 390px)' }}
          pagination={{ current: pagination.page, pageSize: pagination.limit, total: pagination.total, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200], showTotal: total => `${total} recursos` }}
          onChange={(nextPagination: any, _tableFilters: any, sorter: any) => {
            setPagination({ page: nextPagination.current || 1, limit: nextPagination.pageSize || 50, total: pagination.total });
            if (sorter?.field) setFilters(current => ({ ...current, sortBy: sorter.field, sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc' }));
          }}
          onRow={(row: any) => ({
            className: 'cloud-exec__clickable-row',
            role: 'button',
            tabIndex: 0,
            onClick: () => setSelectedResource(row),
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setSelectedResource(row);
              }
            }
          })}
        />
        <GcpResourceDetailDrawer
          resource={selectedResource}
          currency={reconciliation?.currency || 'BRL'}
          open={Boolean(selectedResource)}
          onClose={() => setSelectedResource(null)}
        />
      </>}
    </Drawer>
  );
};

export default GcpInventoryDrawer;
